import { EmbedType } from '@flowus/common/embed-website';
import { BlockType, CollectionViewType } from '@next-space/fe-api-idl';
import { cloneDeep, forIn, keys, values } from 'lodash-es';
import memoize from 'micro-memoize';
import type { FC } from 'react';
import { appI18n } from '@flowus/common/i18n';
import { ListItemType } from 'src/common/components/list-view';
import {
  createColorPickerData,
  DEFAULT_COLOR_PICKER_DATA,
} from 'src/components/color-picker/default-data';
import { DEFAULT_CODE_LANGUAGE } from 'src/editor/editor/plugin/code/const';
import { AIEditType } from 'src/editor/editor/uikit/ai-editor/const';
import { getIsDarkMode } from 'src/hooks/public/use-theme';
import { ossImagesPath } from 'src/image';
import { getState } from 'src/redux/store';
import type { NextBlock } from 'src/redux/types';
import { ViewModel } from 'src/redux/types';
import { getInitials, getPinyin } from 'src/utils/pinyin';
import { stringToLowerCaseAndRemoveSpace } from 'src/utils/string-util';
import { AITranslate, MoreAI } from './ai-sub-menu';
import { MenuCategoryPinyin } from './const';
import { getEmbedThirdParty } from './embeds-data';
import { InlineContentType, MenuCategory, MenuListActionType } from './types';
import { getLastAlignGravity } from '../align-panel';
import { getLastCodeLanguage } from 'src/utils/block-utils';
import { textToSegments } from 'src/editor/utils/editor';
import { FeatureFlags } from 'src/feature-flags';
import { Icon } from 'src/common/components/icon';

export interface BlockMenuTypes {
  type: ListItemType;
  isHidden?: boolean;
  data: {
    type?: BlockType;
    actionType: MenuListActionType;
    icon?: string;
    title?: string;
    context?: string;
    keywords: string[];
    py?: string;
    quick?: string;
    initials?: string;
    params?: {
      data?: {
        level?: number;
        display?: string;
        format?: any;
      };
      backgroundColor?: string;
      textColor?: string;
      local?: boolean;
    };
  };
}

export const createMenuDataContext: FC<{ title: string; image?: string }> = (props) => {
  const { title, image } = props;
  return (
    <div className="p-2 bg-grey1 dark:bg-grey6 text-t4-medium text-white rounded w-40">
      {image && (
        <img
          src={`${ossImagesPath}/block-description/${image}.png`}
          alt={title}
          className="mb-2 w-full bg-white rounded-sm"
        />
      )}
      <span>{title}</span>
    </div>
  );
};

// #region 普通block列表，拆出来给turninto复用
const basicBlock = {
  textarea: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.TEXTAREA,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockText',
      title: appI18n.t('editor.menu.block.textarea'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.textarea_desc'),
        image: 'textarea',
      }),
      keywords: ['text'],
    },
  },
  todo: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.TODO,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockTodo',
      title: appI18n.t('common.todo_list'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.todo_desc'),
        image: 'todo',
      }),
      keywords: ['todo'],
    },
  },
  subPage: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.PAGE,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockPage',
      title: appI18n.t('common.page'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.page_desc'),
        image: 'page',
      }),
      keywords: ['page'],
    },
  },
  linkPage: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.REFERENCE,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertLinktopage',
      title: appI18n.t('editor.menu.block.link_page'),
      keywords: ['linkPage'],
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.link_page_desc'),
        image: 'link_page',
      }),
      params: {
        data: { display: 'linkPage' },
      },
    },
  },
  h1: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockH1',
      quick: 'h1',
      title: appI18n.t('editor.menu.block.heading1'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.heading1_desc'),
        image: 'h1',
      }),
      keywords: ['h1', 'heading1'],
      params: {
        data: { level: 1 },
      },
    },
  },
  h2: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockH2',
      title: appI18n.t('editor.menu.block.heading2'),
      quick: 'h2',
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.heading2_desc'),
        image: 'h2',
      }),
      keywords: ['h2', 'heading2'],
      params: {
        data: { level: 2 },
      },
    },
  },
  h3: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockH3',
      title: appI18n.t('editor.menu.block.heading3'),
      quick: 'h3',
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.heading3_desc'),
        image: 'h3',
      }),
      keywords: ['h3', 'heading3'],
      params: {
        data: { level: 3 },
      },
    },
  },
  h4: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockH4',
      title: appI18n.t('editor.menu.block.heading4'),
      quick: 'h4',
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.heading4_desc'),
        image: 'h4',
      }),
      keywords: ['h4', 'heading4'],
      params: {
        data: { level: 4 },
      },
    },
  },
  table: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.TABLE,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertTable',
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.table_desc'),
        image: 'table',
      }),
      title: appI18n.t('editor.menu.block.table'),
      keywords: ['simple table', 'table'],
    },
  },
  list: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.LIST,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockBullrted',
      title: appI18n.t('common.bullet_list'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.list_desc'),
        image: 'list',
      }),
      keywords: ['list', 'bulleted list'],
    },
  },
  orderList: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.ORDER_LIST,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockNum',
      title: appI18n.t('common.numbered_list'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.numbered_list_desc'),
        image: 'order_list',
      }),
      keywords: ['numbered list'],
    },
  },
  foldList: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.FOLD_LIST,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockToggle',
      title: appI18n.t('common.toggle_list'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.toggle_list_desc'),
        image: 'fold_list',
      }),
      keywords: ['toggle list'],
    },
  },
  divider: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.DIVIDER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertDivider',
      title: appI18n.t('editor.menu.block.divider'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.divider_desc'),
        image: 'divider',
      }),
      keywords: ['divider'],
    },
  },
  quote: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.QUOTE,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertQuote',
      title: appI18n.t('editor.menu.block.quote'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.quote_desc'),
        image: 'quote',
      }),
      keywords: ['quote'],
    },
  },
  mark: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.MARK,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertCallout',
      title: appI18n.t('editor.menu.block.callout'),
      py: 'zhuozhongwenzi',
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.callout_desc'),
        image: 'mark',
      }),
      keywords: ['call out mark'],
    },
  },
  code: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.CODE,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockCode',
      title: appI18n.t('editor.menu.block.code'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.code_desc'),
        image: 'code',
      }),
      keywords: ['code block'],
      params: {
        local: true,
        data: {
          format: {
            language: getLastCodeLanguage() || DEFAULT_CODE_LANGUAGE,
          },
        },
      },
    },
  },
  equation: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.EQUATION,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertEquation',
      title: appI18n.t('common.math_formula'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.equation_desc'),
        image: 'equation',
      }),
      keywords: ['math equation latex'],
      params: {
        local: true,
      },
    },
  },
  mindMap: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.MIND_MAPPING,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertMindMap',
      title: appI18n.t('common.mind_map'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.mindmap_desc'),
        image: 'mind-map',
      }),
      keywords: ['embed mindmap brainmap naotu nt'],
      params: {
        local: true,
      },
    },
  },
  mindMapPage: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.MIND_MAPPING_PAGE,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertMindMap',
      title: appI18n.t('editor.menu.block.mindmap_page'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.mindmap_page_desc'),
        image: 'mind-map',
      }),
      keywords: ['mindmap brainmap naotu nt'],
      params: {
        local: true,
      },
    },
  },
  toggleHeader1: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.TOGGLE_HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockToggleH1',
      title: appI18n.t('editor.menu.block.toggle_heading1'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.toggle_heading1_desc'),
        image: 'toggle-h1',
      }),
      keywords: ['toggle heading h1'],
      params: {
        data: { level: 1 },
      },
    },
  },

  toggleHeader2: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.TOGGLE_HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockToggleH2',
      title: appI18n.t('editor.menu.block.toggle_heading2'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.toggle_heading2_desc'),
        image: 'toggle-h2',
      }),
      keywords: ['toggle heading h2'],
      params: {
        data: { level: 2 },
      },
    },
  },
  toggleHeader3: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.TOGGLE_HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockToggleH3',
      title: appI18n.t('editor.menu.block.toggle_heading3'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.toggle_heading3_desc'),
        image: 'toggle-h3',
      }),
      keywords: ['toggle heading h3'],
      params: {
        data: { level: 3 },
      },
    },
  },
  toggleHeader4: {
    type: ListItemType.BLOCK_ITEM,
    data: {
      type: BlockType.TOGGLE_HEADER,
      actionType: MenuListActionType.CREATE,
      icon: 'MIcInsertBlockToggleH4',
      title: appI18n.t('editor.menu.block.toggle_heading4'),
      context: createMenuDataContext({
        title: appI18n.t('editor.menu.block.toggle_heading4_desc'),
        image: 'toggle-h4',
      }),
      keywords: ['toggle heading h4'],
      params: {
        data: { level: 4 },
      },
    },
  },
};
export const cloneBasicBlock = cloneDeep(basicBlock);
// #endregion
/** 获取BlockMenu */
const buildBlockMenuList = (isDark: boolean) => {
  const aiAssistantWithEmptyDoc = {
    writing: {
      type: ListItemType.BLOCK_ITEM,
      data: {
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcAiFill',
        iconClassName: 'text-ai_color',
        title: appI18n.t('common.product_ai_title'),
        keywords: ['Ask AI to write...'],
      },
    },
    brainstorm: {
      type: ListItemType.BLOCK_ITEM,
      data: {
        aiEditType: AIEditType.Brainstorm,
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcEdit',
        title: appI18n.t('editor.menu.ai.brainstorm'),
        keywords: ['brainstorm ideas'],
      },
    },
    blog: {
      type: ListItemType.BLOCK_ITEM,
      data: {
        aiEditType: AIEditType.Blog,
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcEdit',
        title: appI18n.t('editor.menu.ai.blog'),
        keywords: ['blog posts'],
      },
    },
    more: {
      type: ListItemType.OPERATION,
      data: {
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcMore',
        title: appI18n.t('common.more'),
        keywords: ['more'],
        hasArrow: true,
        renderSubMenu: () => {
          return <MoreAI />;
        },
      },
    },
  };

  const aiAssistantWithNotEmptyDoc = {
    writing: {
      type: ListItemType.BLOCK_ITEM,
      data: {
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcAiFill',
        iconClassName: 'text-ai_color',
        title: appI18n.t('common.product_ai_title'),
        keywords: ['Ask AI to write...'],
      },
    },
    continueWriting: {
      type: ListItemType.BLOCK_ITEM,
      data: {
        aiEditType: AIEditType.ContinueWrite,
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcEdit',
        title: appI18n.t('common.continue_writing'),
        keywords: ['Continue writing'],
      },
    },
    translate: {
      type: ListItemType.OPERATION,
      data: {
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcTranslate',
        title: appI18n.t('common.translate'),
        keywords: ['Translate'],
        hasArrow: true,
        renderSubMenu: () => {
          return <AITranslate />;
        },
      },
      attribute: {
        'data-prevent-event': true,
      },
    },
    more: {
      type: ListItemType.OPERATION,
      data: {
        actionType: MenuListActionType.AI_EDITOR,
        icon: 'IcMore',
        title: appI18n.t('common.more'),
        keywords: ['more'],
        hasArrow: true,
        renderSubMenu: () => {
          return <MoreAI hasContent={true} />;
        },
      },
      attribute: {
        'data-prevent-event': true,
      },
    },
  };

  // #region 高级块
  const advanced = {
    template: {
      type: ListItemType.BLOCK_ITEM,
      data: {
        type: BlockType.TEMPLATE,
        actionType: MenuListActionType.CREATE,
        icon: 'MIcInsertBlockTemplateButton',
        title: appI18n.t('common.template_button'),
        py: 'mubananniu',
        context: createMenuDataContext({
          title: appI18n.t('editor.menu.advanced.template_desc'),
          image: 'template',
        }),
        keywords: ['template button'],
      },
    },
    sync: {
      type: ListItemType.BLOCK_ITEM,
      data: {
        type: BlockType.SYNC_CONTAINER,
        actionType: MenuListActionType.CREATE,
        icon: 'MIcInsertSyncedBlock',
        title: appI18n.t('common.sync_block'),
        context: createMenuDataContext({
          title: appI18n.t('editor.menu.advanced.sync_desc'),
          image: 'sync',
        }),
        keywords: ['sync'],
      },
    },
    ad: {
      type: ListItemType.BLOCK_ITEM,
      isHidden: false,
      data: {
        type: BlockType.AD,
        actionType: MenuListActionType.CREATE,
        icon: 'MIcInsertSyncedBlock',
        title: appI18n.t('editor.menu.advanced.ad'),
        keywords: ['ad'],
      },
    },
    chart: {
      type: ListItemType.BLOCK_ITEM,
      isHidden: !FeatureFlags.CHART,
      data: {
        type: BlockType.CHART,
        actionType: MenuListActionType.CREATE,
        icon: 'IcChartLine',
        title: appI18n.t('editor.menu.advanced.chart'),
        keywords: ['chart'],
        params: {
          data: { segments: textToSegments(appI18n.t('editor.menu.type.chart') as string) },
          local: true,
        },
      },
    },
  };
  // #endregion

  // #region 转换为列表
  const turninto: Record<string, typeof basicBlock> = {};

  Object.keys(basicBlock).forEach((key) => {
    if (!['divider', 'linkPage', 'table', 'mindMapPage'].includes(key)) {
      // @ts-ignore line
      const item = basicBlock[key];
      turninto[key] = {
        ...item,
        data: {
          ...item.data,
          actionType: MenuListActionType.TURN_INTO,
        },
      };
    }
  });
  // #endregion

  // #region 文字颜色 背景颜色 上次使用
  const [textColorMenuList, bgColorMenuList] = createColorPickerData(
    appI18n.t
  ).DEFAULT_COLOR_PICKER_DATA.map((color) =>
    color.items.map((item) => {
      const isText = color.title === appI18n.t('components.color_picker.text_color');

      return {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.COLOR,
          // 展示icon
          ...(isText
            ? { textColor: item.colorkey || 'default' }
            : { backgroundColor: item.colorkey || 'default' }),
          // 标题
          title: item.desc,
          // 关键词
          keywords: [
            item.colorkey ? `${isText ? '' : 'bg '}${item.colorkey.split('_')[0]}` : 'default',
            // 英文关键词
            item.colorkey
              ? `${isText ? '' : 'background '}${item.colorkey.split('_')[0]}`
              : 'default',
          ],
          // 给到block的字段属性
          params: {
            ...(isText
              ? { backgroundColor: '', textColor: item.colorkey || '' }
              : { backgroundColor: item.colorkey || '', textColor: '' }),
          },
        },
      };
    })
  );
  // #endregion

  // 最后一次使用的颜色
  const lastUsedColor = {
    type: ListItemType.BLOCK_ITEM,
    data: {
      actionType: MenuListActionType.COLOR,
      title: appI18n.t('common.last_used_color'),
    },
  };

  const BlockMenu = {
    aiAssistantWithEmptyDoc,
    aiAssistantWithNotEmptyDoc,
    basicBlock,
    media: {
      image: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.FILE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockImage',
          title: appI18n.t('common.image'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.media.image_desc'),
            image: 'image',
          }),
          keywords: ['image', 'picture'],
          params: {
            data: {
              display: 'image',
              format: {
                contentGravity: getLastAlignGravity(),
              },
            },
            local: true,
          },
        },
      },

      bookmark: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.BOOKMARK,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockBookmark',
          title: appI18n.t('editor.menu.media.bookmark'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.media.bookmark_desc'),
            image: 'bookmark',
          }),
          keywords: ['web bookmark'],
          params: {
            local: true,
          },
        },
      },
      video: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.FILE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockVideo',
          title: appI18n.t('common.video_text'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.media.video_desc'),
            image: 'video',
          }),
          keywords: ['video'],
          params: {
            data: {
              display: 'video',
            },
            local: true,
          },
        },
      },
      audio: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.FILE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockAudio',
          title: appI18n.t('common.audio'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.media.audio_desc'),
            image: 'audio',
          }),
          keywords: ['audio'],
          params: {
            data: {
              display: 'audio',
            },
            local: true,
          },
        },
      },
      file: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.FILE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockFile',
          title: appI18n.t('common.file'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.media.file_desc'),
            image: 'file',
          }),
          keywords: ['file'],
          params: {
            local: true,
            data: {
              display: 'file',
            },
          },
        },
      },
      folder: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.FOLDER,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockFolder',
          title: appI18n.t('common.folder'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.media.folder_desc'),
            image: 'folder',
          }),
          keywords: ['folder'],
          params: { local: true },
        },
      },
    },
    inlineElement: {
      date: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.CREATE,
          type: InlineContentType.DATE,
          icon: 'MIcInsertBlockDate',
          title: appI18n.t('editor.menu.inline.date'),
          isInline: true,
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.inline.date_desc'),
            image: 'inline_date',
          }),
          keywords: ['inline date inline time'],
        },
      },
      link_page: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.CREATE,
          type: InlineContentType.LINK_PAGE,
          icon: 'MIcInsertPageInline',
          title: appI18n.t('editor.menu.inline.link_page'),
          isInline: true,
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.inline.link_page_desc'),
            image: 'inline_link_page',
          }),
          keywords: ['inline linkPage'],
        },
      },
      page: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.CREATE,
          type: InlineContentType.PAGE,
          icon: 'MIcInsertSubpage',
          title: appI18n.t('editor.menu.inline.page'),
          isInline: true,
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.inline.page_desc'),
            image: 'inline_page',
          }),
          keywords: ['inline page', 'hangneiyemian', 'hnym', 'hnys hnym', 'hnys inline Page'],
        },
      },
      equation: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.CREATE,
          type: InlineContentType.EQUATION,
          icon: 'MIcInlineEquation',
          title: appI18n.t('editor.menu.inline.equation'),
          isInline: true,
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.inline.equation_desc'),
            image: 'inline_equation',
          }),
          keywords: ['inline equation'],
        },
      },
      person: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.CREATE,
          type: InlineContentType.PEOPLE,
          icon: 'MIcMentionPerson',
          title: appI18n.t('common.person'),
          isInline: true,
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.inline.person_desc'),
            image: 'inline_person',
          }),
          keywords: ['inline person'],
        },
      },
      emoji: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.CREATE,
          type: InlineContentType.EMOJI,
          image: new URL(
            `${ossImagesPath}/embeds/${isDark ? 'dark' : 'light'}/ic_insert_emoji.png`,
            import.meta.url
          ).href,
          title: 'Emoji',
          isInline: true,
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.inline.emoji_desc'),
            image: 'inline_emoji',
          }),
          keywords: ['inline emoji'],
        },
      },
    },
    database: {
      embedBitable: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertDatabase',
          title: appI18n.t('common.table'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.table_desc'),
            image: 'embed_bitable',
          }),
          keywords: ['database', 'table'],
          tableType: CollectionViewType.TABLE,
        },
      },
      bitablePage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW_PAGE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockDatabase',
          title: appI18n.t('editor.menu.database.table_page'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.table_page_desc'),
            image: 'bitable_page',
          }),
          keywords: ['database page', 'table page'],
          tableType: CollectionViewType.TABLE,
        },
      },
      embedBitableBoard: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBoard',
          title: appI18n.t('common.board'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.board_desc'),
            image: 'embed_bitable_board',
          }),
          keywords: ['database board', 'database board'],
          tableType: CollectionViewType.BOARD,
        },
      },
      bitableBoardPage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW_PAGE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockBoard',
          title: appI18n.t('editor.menu.database.board_page'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.board_page_desc'),
            image: 'bitable_board_page',
          }),
          keywords: ['database board page', 'database board page'],
          tableType: CollectionViewType.BOARD,
        },
      },
      embedBitableGallery: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertGallery',
          title: appI18n.t('common.gallery'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.gallery_desc'),
            image: 'embed_bitable_gallery',
          }),
          keywords: ['database gallery', 'database gallery'],
          tableType: CollectionViewType.GALLERY,
        },
      },
      bitableGalleryPage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW_PAGE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockGallery',
          title: appI18n.t('editor.menu.database.gallery_page'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.gallery_page_desc'),
            image: 'bitable_gallery_page',
          }),
          keywords: ['database gallery page', 'database gallery page'],
          tableType: CollectionViewType.GALLERY,
        },
      },
      embedBitableList: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertList',
          title: appI18n.t('common.directory'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.list_desc'),
            image: 'embed_bitable_list',
          }),
          keywords: ['database list', 'database list'],
          tableType: CollectionViewType.LIST,
        },
      },
      bitableListPage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW_PAGE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockList',
          title: appI18n.t('editor.menu.database.list_page'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.list_page_desc'),
            image: 'bitable_list_page',
          }),
          keywords: ['database list page', 'database list page'],
          tableType: CollectionViewType.LIST,
        },
      },
      embedBitableTimeline: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertTimeline',
          title: appI18n.t('common.timeline'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.timeline_desc'),
            image: 'embed_bitable_timeline',
          }),
          keywords: ['database timeline', 'database timeline'],
          tableType: CollectionViewType.TIMELINE,
        },
      },
      bitableTimelinePage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW_PAGE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockTimeline',
          title: appI18n.t('editor.menu.database.timeline_page'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.timeline_page_desc'),
            image: 'bitable_timeline_page',
          }),
          keywords: ['database timeline page', 'database timeline page'],
          tableType: CollectionViewType.TIMELINE,
        },
      },
      embedBitableForm: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockForm',
          title: appI18n.t('common.form'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.form_desc'),
            image: 'embed_bitable_form',
          }),
          keywords: ['database form', 'database form'],
          tableType: CollectionViewType.FORM,
        },
      },
      bitableFormPage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW_PAGE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertForm',
          title: appI18n.t('editor.menu.database.form_page'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.form_page_desc'),
            image: 'bitable_form_page',
          }),
          keywords: ['database form page', 'database form page'],
          tableType: CollectionViewType.FORM,
        },
      },
      embedBitableCalendar: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertCalendar',
          title: appI18n.t('common.calendar'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.calendar_desc'),
            image: 'embed_bitable_calendar',
          }),
          keywords: ['database calendar', 'database calendar'],
          tableType: CollectionViewType.CALENDAR,
        },
      },
      bitableCalendarPage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLLECTION_VIEW_PAGE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockCalendar',
          title: appI18n.t('editor.menu.database.calendar_page'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.calendar_page_desc'),
            image: 'bitable_calendar_page',
          }),
          keywords: ['database calendar page', 'database calendar page'],
          tableType: CollectionViewType.CALENDAR,
        },
      },
      linkBitable: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.REFERENCE_COLLECTION,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertPageInline',
          title: appI18n.t('editor.menu.database.linked'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.database.linked_desc'),
            image: 'link_bitable',
          }),
          keywords: ['linked database', 'link table'],
        },
      },
    },
    embed: {
      webpage: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.EMBED,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockWeb',
          title: appI18n.t('editor.menu.embed.webpage'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.embed.webpage_desc'),
            image: 'embed_webpage',
          }),
          keywords: ['embed web iframe'],
          params: {
            data: {
              embedType: EmbedType.webpage,
            },
            local: true,
          },
        },
      },
      file: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.FILE,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertPdf',
          title: appI18n.t('editor.menu.embed.file'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.embed.file_desc'),
            image: 'embed_file',
          }),
          keywords: ['embed file'],
          params: {
            data: {
              display: 'file',
              viewMode: ViewModel.preview,
            },
            local: true,
          },
        },
      },
      folder: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.FOLDER,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertFolder',
          title: appI18n.t('editor.menu.embed.folder'),
          context: createMenuDataContext({
            title: appI18n.t('editor.menu.embed.folder_desc'),
            image: 'embed_folder',
          }),
          keywords: ['embed folder'],
          params: {
            data: { viewMode: ViewModel.preview },
            local: true,
          },
        },
      },
      ...getEmbedThirdParty(),
    },
    advanced,
    layout: {
      leftColumn: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLUMN_LIST,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockLeft',
          title: appI18n.t('editor.menu.layout.left_column'),
          params: {
            left: true,
          },
        },
      },
      rightColumn: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLUMN_LIST,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertBlockRight',
          title: appI18n.t('editor.menu.layout.right_column'),
          params: {
            left: false,
          },
        },
      },
      twoColumn: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLUMN_LIST,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertDownTwo',
          quick: '2',
          title: appI18n.t('editor.menu.layout.two_columns'),
          params: {
            columnCount: 2,
          },
        },
      },
      threeColumn: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLUMN_LIST,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertDownThree',
          quick: '3',
          title: appI18n.t('editor.menu.layout.three_columns'),
          params: {
            columnCount: 3,
          },
        },
      },
      fourColumn: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLUMN_LIST,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertDownFour',
          quick: '4',
          title: appI18n.t('editor.menu.layout.four_columns'),
          params: {
            columnCount: 4,
          },
        },
      },
      fiveColumn: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          type: BlockType.COLUMN_LIST,
          actionType: MenuListActionType.CREATE,
          icon: 'MIcInsertDownFive',
          quick: '5',
          title: appI18n.t('editor.menu.layout.five_columns'),
          params: {
            columnCount: 5,
          },
        },
      },
    },
    turninto,
    action: {
      delete: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.DELETE,
          icon: 'IcTrash',
          keywords: ['delete'],
          title: appI18n.t('common.delete'),
          iconSize: 'small',
        },
      },
      copy: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.COPY,
          icon: 'IcDuplicate',
          keywords: ['copy'],
          title: appI18n.t('common.make_copy'),
          iconSize: 'small',
        },
      },
      moveTo: {
        type: ListItemType.BLOCK_ITEM,
        data: {
          actionType: MenuListActionType.MOVETO,
          icon: 'IcMove',
          keywords: ['move'],
          title: appI18n.t('common.move_to'),
          iconSize: 'small',
        },
      },
    },
    lastUsedColor: { lastUsedColor },
    color: textColorMenuList,
    colorBackground: bgColorMenuList,
  };
  const bigIconBlockTypes: Set<keyof typeof BlockMenu> = new Set([
    'basicBlock',
    'media',
    'inlineElement',
    'database',
    'embed',
    'advanced',
    'layout',
  ]);
  // #region BlockMenu内容转拼音
  // @ts-ignore next line
  forIn(BlockMenu, (menuType, blockType: keyof typeof BlockMenu) => {
    forIn(menuType, (value: any) => {
      // 首字母
      // @ts-ignore: next line
      value.data.initials = value.data.initials ?? getInitials(value.data.title);
      // 拼音
      // @ts-ignore: next line
      value.data.py = value.data.py ?? getPinyin(value.data.title);

      // @ts-ignore: for tracker
      value.data.category = MenuCategory[blockType];

      // if (bigIconBlockTypes.has(blockType) && value.type === ListItemType.BLOCK_ITEM) {
      //   if (typeof value.data.icon === 'string') {
      //     const iconName = value.data.icon;
      //     value.data.icon = (
      //       <Icon
      //         name={iconName}
      //         size="midMax"
      //         className="rounded border border-grey6 text-white"
      //       />
      //     );
      //     value.data.transformIcon = (
      //       <Icon
      //         name={iconName}
      //         size="xxlarge"
      //         className="rounded border border-grey6 text-white mr-2"
      //       />
      //     );
      //   }
      // }
    });
  });
  // #endregion

  return BlockMenu;
};

const buildMemoBlockMenuList = memoize(buildBlockMenuList);

/** 优化block list的搜索列表数据 */
const buildMenuListMemo = memoize((isDark: boolean) => {
  const menuList = buildBlockMenuList(isDark);
  // @ts-ignore type
  keys(menuList).forEach((blockType: keyof typeof menuList) => {
    const menus = menuList[blockType];
    const category = MenuCategory[blockType];
    const categoryInfo = MenuCategoryPinyin[blockType];
    keys(menus).forEach((menuKey) => {
      // @ts-ignore type
      const menu = menuList[blockType][menuKey];
      if (menu?.data) {
        // @ts-ignore type
        menuList[blockType][menuKey].data.keywords = [
          ...new Set([
            menu.data.quick,
            menu.data.initials,
            ...(menu.data.keywords || []),
            menu.data.py,
            menu.data.title,
            // 分类首字母+标题首字母
            `${categoryInfo.initials} ${menu.data.initials}`,
            // 分类+关键词
            ...(menu.data.keywords || []).map((k: string) => `${categoryInfo.initials} ${k}`),
          ]),
        ].filter(Boolean) as string[];

        // 关键词词组
        // @ts-ignore type
        menuList[blockType][menuKey].data.thesaurus = [
          `${blockType} ${menu.data.title}`,
          `${blockType} ${menu.data.quick}`,
          // 块分组类型
          blockType,
          // 关键词
          ...(menu.data.keywords || []),
          categoryInfo.pinyin,
          // 分类+块标题
          `${category} ${menu.data.title}`,
          `${category} ${menu.data.quick}`,
        ].map((s) => stringToLowerCaseAndRemoveSpace(s));
      }
    });
  });
  return menuList;
});

export const getMemoBlockMenuList = () => {
  const isDark = getIsDarkMode();
  const blockMenu = buildMenuListMemo(isDark);
  return blockMenu;
};

export const getBlockMenuList = () => {
  const isDark = getIsDarkMode();
  const blockMenu = buildMemoBlockMenuList(isDark);
  return blockMenu;
};

export const cacheBlockMenu: Record<string, BlockMenuTypes['data'] | undefined> = {};

export const getBlockMenu = (
  blockType?: BlockType,
  params?: {
    level?: number;
    display?: string;
  }
) => {
  const key = `${blockType}-${params?.level}-${params?.display}`;

  if (cacheBlockMenu[key]) {
    return cacheBlockMenu[key];
  }

  if (blockType === undefined) return;
  let result: BlockMenuTypes['data'] | undefined;
  const blockMenuList = getBlockMenuList();
  values(blockMenuList).some((menus) => {
    menus = values(menus);
    const menuIndex = menus.findIndex((item: BlockMenuTypes) => {
      if (item.data.type === blockType) {
        if (
          blockType !== BlockType.HEADER &&
          blockType !== BlockType.FILE &&
          blockType !== BlockType.TOGGLE_HEADER
        ) {
          return true;
        }
        if (params?.level && params.level === item.data.params?.data?.level) {
          return true;
        }
        if (params?.display && params.display === item.data.params?.data?.display) {
          return true;
        }
      }
      return false;
    });
    const menu = menus[menuIndex];
    if (menu) {
      result = menu.data;
      return true;
    }
    return false;
  });

  cacheBlockMenu[key] = result;

  return result;
};

// #region 埋点用，把block换成对应的文案
export const covertBlockToTitle = (uuid: string) => {
  const block = getState().blocks[uuid];
  const BlockMenu = getBlockMenuList();
  if (!block) return '';
  switch (block.type) {
    case BlockType.REFERENCE: {
      if (block.data.display === 'linkPage') {
        return BlockMenu.basicBlock.linkPage.data.title;
      }
      return appI18n.t('editor.menu.type.reference');
    }
    case BlockType.FILE: {
      switch (block.data.display) {
        case 'audio':
          return BlockMenu.media.audio.data.title;
        case 'video':
          return BlockMenu.media.video.data.title;
        case 'image':
          return BlockMenu.media.image.data.title;
        case 'file':
          return block.data.viewMode === ViewModel.preview
            ? BlockMenu.embed.file.data.title
            : BlockMenu.media.file.data.title;
        default:
          return '';
      }
    }
    case BlockType.HEADER: {
      return appI18n.t('editor.menu.type.heading', { level: block.data.level ?? 1 });
    }
    case BlockType.TOGGLE_HEADER: {
      return appI18n.t('common.toggle_heading', { level: block.data.level ?? 1 });
    }
    case BlockType.CHART: {
      return appI18n.t('editor.menu.type.chart');
    }
    default:
  }
  const menus = [
    BlockMenu.basicBlock,
    BlockMenu.media,
    BlockMenu.database,
    BlockMenu.embed,
    BlockMenu.advanced,
  ];
  let ret;
  for (const menu of menus) {
    ret = covertBlockToTitleFromMenu(block, menu);
    if (ret) return ret;
  }
  if (block.type === BlockType.SYNC_REFERENCE) {
    return appI18n.t('editor.menu.type.sync_shadow');
  } else if (block.type === BlockType.COLUMN_LIST) {
    return appI18n.t('editor.menu.type.column');
  } else if (block.type === BlockType.COLUMN) {
    return appI18n.t('editor.menu.type.column');
  }
  return '';
};

const covertBlockToTitleFromMenu = (
  block: NextBlock,
  obj: {
    [key: string]: { data: { type: BlockType; title: string } };
  }
) => {
  for (const [_, item] of Object.entries(obj)) {
    if (block.type === item.data.type) {
      return item.data.title;
    }
  }
  return '';
};
// #endregion

// #region 生成分组标题
export const CreateBlockTextMenu = (title: string, data?: any) => ({
  type: ListItemType.BLOCK_TEXT,
  data: {
    title,
    py: getPinyin(title),
    initials: getInitials(title),
    ...data,
  },
});
// #endregion
